import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiCallJson } from './Api';

// ── Formatting helpers (same as CDPInsightsTab) ──
const fmt = (v, fb = '0') =>
  v == null || v === '' || Number.isNaN(v) ? fb : typeof v === 'number' ? v.toLocaleString() : String(v);
const fmtDec = (v, d = 1, fb = '0') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d);
const fmtPct = (v, d = 1, fb = '0%') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d) + '%';
const fmtSign = (v, d = null, fb = '0') => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return fb;
  const n = Number(v);
  return (n >= 0 ? '+' : '') + (d !== null ? n.toFixed(d) : n.toLocaleString());
};
const fmtMoney = (v, d = 0, fb = '$0') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb
    : '$' + (d > 0 ? Number(v).toFixed(d) : Number(v).toLocaleString());

// ── Intent styling ──
const INTENT_STYLES = {
  customer_profile:    { bg: 'bg-gray-100',    text: 'text-gray-700',    label: 'Customer Profile' },
  campaign_response:   { bg: 'bg-blue-100',    text: 'text-blue-700',    label: 'Campaign Response' },
  campaign_conversion: { bg: 'bg-green-100',   text: 'text-green-700',   label: 'Campaign Conversion' },
  event_stream:        { bg: 'bg-purple-100',  text: 'text-purple-700',  label: 'Event Stream' },
  transaction:         { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Transaction' },
  survey_feedback:     { bg: 'bg-sky-100',     text: 'text-sky-700',     label: 'Survey / Feedback' },
  enrichment:          { bg: 'bg-orange-100',  text: 'text-orange-700',  label: 'Enrichment' },
};

const IntentBadge = ({ intent }) => {
  const s = INTENT_STYLES[intent] || INTENT_STYLES.customer_profile;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════

const CDPEventAnalyticsTab = ({ sessionId, user }) => {
  const [activeView, setActiveView] = useState('outcomes');
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  // Data states
  const [classifications, setClassifications] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventSummary, setEventSummary] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [templates, setTemplates] = useState(null);

  // Filters
  const [eventFilter, setEventFilter] = useState({ campaign_id: '', intent: '', event_type: '' });
  const [eventPage, setEventPage] = useState(0);

  // Classifier
  const [classifySource, setClassifySource] = useState({ type: 'file', id: '', columns: '' });
  const [autoDetectResult, setAutoDetectResult] = useState(null);
  const [classifyForm, setClassifyForm] = useState({ intent: '', campaign_id: '', roles: {} });
  const [classifyMsg, setClassifyMsg] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (activeView === 'outcomes') loadOutcomes();
    if (activeView === 'events') { loadEvents(); loadEventSummary(); }
    if (activeView === 'sources') loadClassifications();
    if (activeView === 'classify') loadTemplates();
  }, [activeView, sessionId]);

  // ── API Calls ──

  const API = `/api/cdp/sessions/${sessionId}`;

  const loadClassifications = async () => {
    try {
      setLoading(true);
      const r = await apiCallJson(`${API}/classifications`);
      if (mountedRef.current) setClassifications(r);
    } catch { } finally { if (mountedRef.current) setLoading(false); }
  };

  const loadEvents = async (page = 0) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50', offset: String(page * 50) });
      if (eventFilter.campaign_id) params.set('campaign_id', eventFilter.campaign_id);
      if (eventFilter.intent) params.set('intent', eventFilter.intent);
      if (eventFilter.event_type) params.set('event_type', eventFilter.event_type);
      const r = await apiCallJson(`${API}/events?${params}`);
      if (mountedRef.current) { setEvents(r); setEventPage(page); }
    } catch { } finally { if (mountedRef.current) setLoading(false); }
  };

  const loadEventSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (eventFilter.campaign_id) params.set('campaign_id', eventFilter.campaign_id);
      const r = await apiCallJson(`${API}/events/summary?${params}`);
      if (mountedRef.current) setEventSummary(r);
    } catch { }
  };

  const loadOutcomes = async () => {
    try {
      setLoading(true);
      const r = await apiCallJson(`${API}/campaign-outcomes`);
      if (mountedRef.current) setOutcomes(r);
    } catch { } finally { if (mountedRef.current) setLoading(false); }
  };

  const loadOutcomeDetail = async (campaignId) => {
    try {
      const r = await apiCallJson(`${API}/campaign-outcomes/${campaignId}`);
      if (mountedRef.current) setSelectedOutcome(r);
    } catch { }
  };

  const computeOutcome = async (campaignId) => {
    try {
      const r = await apiCallJson(`${API}/campaign-outcomes/${campaignId}/compute`, { method: 'POST' });
      if (mountedRef.current) {
        setSelectedOutcome(r);
        loadOutcomes();
      }
    } catch { }
  };

  const loadTemplates = async () => {
    try {
      const r = await apiCallJson(`${API}/classification-templates`);
      if (mountedRef.current) setTemplates(r);
    } catch { }
  };

  const runAutoDetect = async () => {
    try {
      const cols = classifySource.columns.split(',').map(c => c.trim()).filter(Boolean);
      if (cols.length === 0) return;
      const r = await apiCallJson(`${API}/classify/auto-detect`, {
        method: 'POST',
        body: JSON.stringify({ source_type: classifySource.type, source_id: classifySource.id, columns: cols }),
      });
      if (mountedRef.current) {
        setAutoDetectResult(r);
        setClassifyForm(prev => ({ ...prev, intent: r.intent, roles: r.column_roles || {} }));
      }
    } catch { }
  };

  const applyClassification = async () => {
    try {
      setClassifyMsg(null);
      await apiCallJson(`${API}/classify`, {
        method: 'POST',
        body: JSON.stringify({
          source_type: classifySource.type,
          source_id: classifySource.id,
          data_intent: classifyForm.intent,
          linked_campaign_id: classifyForm.campaign_id || null,
          column_roles: classifyForm.roles,
          metric_definitions: autoDetectResult?.suggested_metrics || [],
        }),
      });
      if (mountedRef.current) setClassifyMsg({ type: 'success', text: 'Classification applied successfully.' });
    } catch (e) {
      if (mountedRef.current) setClassifyMsg({ type: 'error', text: e.message || 'Failed to classify' });
    }
  };

  const triggerExtraction = async (sourceType, sourceId) => {
    try {
      await apiCallJson(`${API}/events/extract`, {
        method: 'POST',
        body: JSON.stringify({ source_type: sourceType, source_id: sourceId }),
      });
      loadEvents();
      loadEventSummary();
    } catch { }
  };

  // ── Tabs ──
  const TABS = [
    { id: 'outcomes', label: 'Campaign Outcomes' },
    { id: 'events', label: 'Event Log' },
    { id: 'sources', label: 'Source Classifications' },
    { id: 'classify', label: 'Classify Source' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold text-gray-900">Event Analytics</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Campaign attribution, event tracking, and outcome analysis
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1" aria-label="Event analytics tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`py-2.5 px-3 border-b-2 text-sm font-medium transition-colors ${
                activeView === tab.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div>
        {activeView === 'outcomes' && (
          <OutcomesView
            outcomes={outcomes}
            selectedOutcome={selectedOutcome}
            onSelect={loadOutcomeDetail}
            onCompute={computeOutcome}
          />
        )}
        {activeView === 'events' && (
          <EventLogView
            events={events}
            summary={eventSummary}
            filter={eventFilter}
            setFilter={f => { setEventFilter(f); setTimeout(() => { loadEvents(0); loadEventSummary(); }, 0); }}
            page={eventPage}
            onPageChange={p => loadEvents(p)}
            onExtract={triggerExtraction}
            loading={loading}
          />
        )}
        {activeView === 'sources' && (
          <ClassificationsView
            data={classifications}
            loading={loading}
            onExtract={triggerExtraction}
          />
        )}
        {activeView === 'classify' && (
          <ClassifierView
            source={classifySource}
            setSource={setClassifySource}
            autoResult={autoDetectResult}
            onAutoDetect={runAutoDetect}
            form={classifyForm}
            setForm={setClassifyForm}
            onApply={applyClassification}
            msg={classifyMsg}
            templates={templates}
          />
        )}
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 1. CAMPAIGN OUTCOMES VIEW
// ══════════════════════════════════════════════════════════════

const OutcomesView = ({ outcomes, selectedOutcome, onSelect, onCompute }) => {
  if (!outcomes || outcomes.total === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-400 text-sm">No campaign outcomes computed yet.</p>
        <p className="text-gray-400 text-xs mt-1">Classify and ingest campaign data, then compute outcomes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Outcomes table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Targeted</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Delivered</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Converted</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conv %</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">ROI %</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {outcomes.outcomes.map((o, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{o.campaign_name}</div>
                  <div className="text-xs text-gray-400">
                    {o.status} · {o.from_files || 0} file / {o.from_api || 0} API events
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">{fmt(o.targeted)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">{fmt(o.delivery_rate, '—')}%</td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">{fmt(o.converted)}</td>
                <td className="px-4 py-3 text-sm text-right font-medium">{fmtPct(o.conversion_rate)}</td>
                <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">{fmtMoney(o.revenue)}</td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={Number(o.roi_pct) >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {fmtSign(o.roi_pct, 1)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-center space-x-2">
                  <button onClick={() => onSelect(o.campaign_id)} className="text-xs text-blue-600 hover:underline">
                    View
                  </button>
                  <button onClick={() => onCompute(o.campaign_id)} className="text-xs text-gray-500 hover:underline">
                    Recompute
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Selected outcome detail */}
      {selectedOutcome && selectedOutcome.computed !== false && (
        <OutcomeDetail outcome={selectedOutcome} />
      )}
    </div>
  );
};

const OutcomeDetail = ({ outcome }) => {
  const f = outcome.funnel || {};
  const r = outcome.rates || {};
  const fin = outcome.financials || {};

  // Build funnel steps
  const funnelSteps = [
    { label: 'Targeted', value: f.targeted, rate: null },
    { label: 'Delivered', value: f.delivered, rate: r.delivery_rate },
    { label: 'Opened', value: f.opened, rate: r.open_rate },
    { label: 'Clicked', value: f.clicked, rate: r.click_rate },
    { label: 'Converted', value: f.converted, rate: r.conversion_rate },
  ].filter(s => s.value > 0 || s.label === 'Targeted');

  const maxVal = Math.max(...funnelSteps.map(s => s.value || 0), 1);

  return (
    <div className="bg-white rounded-lg border-2 border-blue-200 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">{outcome.campaign_name}</h4>
          <span className="text-xs text-gray-400">
            v{outcome.baseline_version} → v{outcome.outcome_version}
            {outcome.computed_at && ` · computed ${new Date(outcome.computed_at).toLocaleString()}`}
          </span>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Unique Customers" value={fmt(outcome.unique_customers)} />
        <KPI label="Total Events" value={fmt(outcome.total_events)} />
        <KPI label="Revenue" value={fmtMoney(fin.total_revenue)} accent="text-green-600" />
        <KPI label="Cost" value={fmtMoney(fin.total_cost)} accent="text-red-600" />
        <KPI label="ROI" value={`${fmtSign(fin.roi_percentage, 1)}%`}
          accent={Number(fin.roi_percentage) >= 0 ? 'text-green-600' : 'text-red-600'} />
      </div>

      {/* Visual funnel */}
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Conversion Funnel</div>
        <div className="space-y-2">
          {funnelSteps.map((step, idx) => {
            const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
            return (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-24 text-xs text-gray-600 text-right font-medium">{step.label}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(pct, 2)}%`,
                      background: `linear-gradient(90deg, #3b82f6 0%, ${idx === funnelSteps.length - 1 ? '#10b981' : '#60a5fa'} 100%)`,
                    }}
                  />
                  <span className="absolute inset-0 flex items-center px-3 text-xs font-bold text-white mix-blend-difference">
                    {fmt(step.value)}
                  </span>
                </div>
                <div className="w-16 text-xs text-gray-500 text-right">
                  {step.rate != null ? fmtPct(step.rate) : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bounce / unsub row */}
      {(f.bounced > 0 || f.unsubscribed > 0) && (
        <div className="flex gap-4">
          {f.bounced > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm">
              <span className="text-red-600 font-medium">{fmt(f.bounced)}</span>
              <span className="text-red-500 ml-1">bounced ({fmtPct(r.bounce_rate)})</span>
            </div>
          )}
          {f.unsubscribed > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm">
              <span className="text-amber-600 font-medium">{fmt(f.unsubscribed)}</span>
              <span className="text-amber-500 ml-1">unsubscribed</span>
            </div>
          )}
        </div>
      )}

      {/* Event source breakdown */}
      {outcome.event_sources && (
        <div className="flex gap-4 text-sm">
          <span className="text-gray-500">Event sources:</span>
          <span className="font-medium">{fmt(outcome.event_sources.file)} from files</span>
          <span className="font-medium">{fmt(outcome.event_sources.api)} from API</span>
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 2. EVENT LOG VIEW
// ══════════════════════════════════════════════════════════════

const EventLogView = ({ events, summary, filter, setFilter, page, onPageChange, loading }) => {
  return (
    <div className="space-y-5">
      {/* Summary cards */}
      {summary && summary.has_data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total Events" value={fmt(summary.total_events)} />
          <KPI label="Unique Customers" value={fmt(summary.unique_customers)} />
          <KPI label="Revenue" value={fmtMoney(summary.revenue)} accent="text-green-600" />
          <KPI label="Avg Value" value={fmtMoney(summary.avg_value, 2)} />
        </div>
      )}

      {/* Funnel mini-bar (from summary) */}
      {summary && summary.has_data && summary.funnel && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Event Funnel</div>
          <div className="flex gap-3 flex-wrap">
            {Object.entries(summary.funnel)
              .filter(([, v]) => v > 0)
              .map(([key, val]) => (
                <div key={key} className="bg-white border border-gray-200 rounded px-3 py-1.5">
                  <span className="text-xs text-gray-500 capitalize">{key}</span>
                  <span className="ml-2 text-sm font-bold text-gray-900">{fmt(val)}</span>
                </div>
              ))}
            <div className="bg-white border border-gray-200 rounded px-3 py-1.5">
              <span className="text-xs text-gray-500">Sources</span>
              <span className="ml-2 text-sm font-medium text-gray-700">
                {fmt(summary.sources?.file)} file · {fmt(summary.sources?.api)} API
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Intent</label>
          <select
            value={filter.intent}
            onChange={e => setFilter({ ...filter, intent: e.target.value })}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All intents</option>
            {Object.keys(INTENT_STYLES).filter(k => k !== 'customer_profile').map(k => (
              <option key={k} value={k}>{INTENT_STYLES[k].label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Event Type</label>
          <input
            value={filter.event_type}
            onChange={e => setFilter({ ...filter, event_type: e.target.value })}
            placeholder="e.g. delivered"
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm w-40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Campaign ID</label>
          <input
            value={filter.campaign_id}
            onChange={e => setFilter({ ...filter, campaign_id: e.target.value })}
            placeholder="Filter by campaign"
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm w-48"
          />
        </div>
      </div>

      {/* Event type summary */}
      {events?.summary_by_type && events.summary_by_type.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {events.summary_by_type.slice(0, 10).map((s, i) => (
            <span key={i} className="bg-gray-100 rounded-full px-3 py-1 text-xs font-medium text-gray-700">
              {s.event_type}: {fmt(s.count)} {s.total_value > 0 && `(${fmtMoney(s.total_value)})`}
            </span>
          ))}
        </div>
      )}

      {/* Events table */}
      {events && events.events && events.events.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Intent</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.events.map((ev, idx) => (
                <tr key={idx} className="hover:bg-gray-50 text-sm">
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900">{ev.event_type || '—'}</span>
                  </td>
                  <td className="px-3 py-2"><IntentBadge intent={ev.data_intent} /></td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs truncate max-w-[160px]">
                    {ev.customer_key || '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {ev.event_value != null && ev.event_value > 0 ? fmtMoney(ev.event_value, 2) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {ev.source_type} · v{ev.version_captured}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {ev.event_timestamp ? new Date(ev.event_timestamp).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
            <span className="text-xs text-gray-500">{fmt(events.total)} total events</span>
            <div className="flex gap-2">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>
              <span className="px-2 py-1 text-xs text-gray-500">Page {page + 1}</span>
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={events.events.length < 50}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p className="text-gray-400 text-sm">No events found. Classify a data source and extract events to see data here.</p>
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 3. SOURCE CLASSIFICATIONS VIEW
// ══════════════════════════════════════════════════════════════

const ClassificationsView = ({ data, loading, onExtract }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-400 text-sm">No sources classified yet.</p>
        <p className="text-gray-400 text-xs mt-1">Use the "Classify Source" tab to tag files and API sources.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">{data.total} classified source{data.total !== 1 ? 's' : ''}</div>

      <div className="space-y-3">
        {data.classifications.map((c, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{c.source_name || c.source_id}</span>
                  <IntentBadge intent={c.data_intent} />
                  <span className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{c.source_type}</span>
                </div>
                {c.linked_campaign_name && (
                  <div className="text-xs text-blue-600 mt-1">→ {c.linked_campaign_name}</div>
                )}
                {c.column_roles && Object.keys(c.column_roles).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(c.column_roles).map(([role, col]) => (
                      <span key={role} className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-0.5">
                        <span className="text-gray-400">{role}:</span> <span className="text-gray-700">{col}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                {c.data_intent !== 'customer_profile' && (
                  <button
                    onClick={() => onExtract(c.source_type, c.source_id)}
                    className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Extract Events
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-2 text-xs text-gray-400">
              <span>{c.classification_method}</span>
              {c.confidence_score && <span>confidence: {fmtPct(c.confidence_score * 100, 0)}</span>}
              {c.created_at && <span>{new Date(c.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 4. DATA SOURCE CLASSIFIER
// ══════════════════════════════════════════════════════════════

const ClassifierView = ({ source, setSource, autoResult, onAutoDetect, form, setForm, onApply, msg, templates }) => {
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Classify a file or API source by its data intent so the system knows how to extract and attribute events.
      </p>

      {/* Step 1: Source identification */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Step 1: Identify Source</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Source Type</label>
            <select
              value={source.type}
              onChange={e => setSource({ ...source, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="file">File Upload</option>
              <option value="api">API Source</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Source ID</label>
            <input
              value={source.id}
              onChange={e => setSource({ ...source, id: e.target.value })}
              placeholder={source.type === 'file' ? 'file_id' : 'api_source_id'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Column Names (comma-separated)</label>
            <input
              value={source.columns}
              onChange={e => setSource({ ...source, columns: e.target.value })}
              placeholder="email, status, delivered_at, order_total"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
        <button
          onClick={onAutoDetect}
          disabled={!source.columns}
          className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-40 transition-colors"
        >
          Auto-Detect Intent
        </button>
      </div>

      {/* Auto-detect results */}
      {autoResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 space-y-4">
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Auto-Detection Result</div>

          <div className="flex items-center gap-3">
            <IntentBadge intent={autoResult.intent} />
            <span className="text-sm text-gray-600">
              Confidence: <span className="font-bold">{fmtPct(autoResult.confidence * 100, 0)}</span>
            </span>
          </div>

          {autoResult.matched_columns && autoResult.matched_columns.length > 0 && (
            <div className="text-xs text-gray-600">
              Matched columns: {autoResult.matched_columns.join(', ')}
            </div>
          )}

          {autoResult.column_roles && Object.keys(autoResult.column_roles).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Detected Column Roles</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(autoResult.column_roles).map(([role, col]) => (
                  <span key={role} className="text-xs bg-white border border-blue-200 rounded px-2 py-1">
                    <span className="text-blue-500">{role}</span> → <span className="font-medium">{col}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {autoResult.matching_templates && autoResult.matching_templates.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Matching Templates</div>
              <div className="flex flex-wrap gap-2">
                {autoResult.matching_templates.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => setForm({
                      ...form,
                      intent: t.data_intent,
                      roles: typeof t.column_roles === 'string' ? JSON.parse(t.column_roles) : (t.column_roles || {}),
                    })}
                    className="text-xs bg-white border border-blue-200 rounded px-3 py-1.5 hover:bg-blue-50 transition-colors"
                  >
                    {t.template_name} ({t.match_score} matches)
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Confirm and apply */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Step 2: Confirm Classification</div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Data Intent</label>
            <select
              value={form.intent}
              onChange={e => setForm({ ...form, intent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Select intent…</option>
              {Object.entries(INTENT_STYLES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link to Campaign (optional)</label>
            <input
              value={form.campaign_id || ''}
              onChange={e => setForm({ ...form, campaign_id: e.target.value })}
              placeholder="campaign_id"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>

        {/* Column role overrides */}
        <div>
          <div className="text-xs font-medium text-gray-700 mb-2">Column Roles</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['customer_key', 'event_type', 'event_value', 'event_timestamp', 'conversion_flag'].map(role => (
              <div key={role}>
                <label className="block text-[10px] text-gray-400 mb-0.5">{role}</label>
                <input
                  value={form.roles?.[role] || ''}
                  onChange={e => setForm({ ...form, roles: { ...form.roles, [role]: e.target.value } })}
                  placeholder="column name"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onApply}
            disabled={!form.intent || !source.id}
            className="px-5 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 transition-colors"
          >
            Apply Classification
          </button>
          {msg && (
            <span className={`text-sm ${msg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {msg.text}
            </span>
          )}
        </div>
      </div>

      {/* Available templates */}
      {templates && templates.templates && templates.templates.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Available Templates</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {templates.templates.map((t, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors cursor-pointer"
                onClick={() => setForm({
                  ...form,
                  intent: t.data_intent,
                  roles: typeof t.column_roles === 'string' ? JSON.parse(t.column_roles) : (t.column_roles || {}),
                })}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{t.template_name}</span>
                  <IntentBadge intent={t.data_intent} />
                  {t.is_system && <span className="text-[10px] text-gray-400">system</span>}
                </div>
                {t.description && <p className="text-xs text-gray-500 mt-1">{t.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ── Shared components ──

const KPI = ({ label, value, accent }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3">
    <div className="text-[11px] text-gray-500 mb-0.5">{label}</div>
    <div className={`text-lg font-bold ${accent || 'text-gray-900'}`}>{value}</div>
  </div>
);

export default CDPEventAnalyticsTab;
